import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Container,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import { styled } from '@mui/system';
import { submitTrialForm } from '../../apis/contactApi';

import Navbar from '../mainPage/components/Navbar';
const StyledTextField = styled(TextField)(({ multiline }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '2px',
    transition: 'all 0.2s ease-in-out',
    height: multiline ? 'auto' : 38,
    alignItems: 'flex-start',

    '& fieldset': {
      border: '0.5px solid #e5e7eb',
    },
    '&:hover fieldset': {
      border: '0.5px solid #d1d5db',
    },
    '&.Mui-focused fieldset': {
      border: '0.6px solid #5965A6',
    },
  },
  '& .MuiInputBase-input': {
    padding: multiline ? '10px 14px' : '10px 14px',
    fontSize: '0.9rem',
    fontWeight: 400,
    color: '#374151',
  },
}));


const FreeTrial = () => {
  // const navigate = useNavigate();
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [form, setForm] = useState({ name: '', email: '', goals: '' });
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState({ open: false, type: 'success', msg: '' });
  
    const handleChange = (e) => {
      const { name, value } = e.target;
      setForm((f) => ({ ...f, [name]: value }));
    };
  
const handleSubmit = async (e) => { 
  e.preventDefault();

  if (!form.name.trim() || !form.email.trim()) {
    setNotice({
      open: true,
      type: 'error',
      msg: 'Please fill in name and email.',
    });
    return;
  }

  setLoading(true);
  try {
    await submitTrialForm({
      name: form.name,
      email: form.email,
      goals: form.goals,
    });

    setNotice({
      open: true,
      type: 'success',
      msg: 'Form submitted successfully.',
    });

    // Reset form
    setForm({ name: '', email: '', goals: '' });
  } catch (err) {
    console.error(err);
    setNotice({
      open: true,
      type: 'error',
      msg: err.response?.data?.message || 'Something went wrong. Please try again.',
    });
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    const handleMouseMove = (e) => {
      const contactSection = document.getElementById('trial-section');
      if (contactSection) {
        const rect = contactSection.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setMousePosition({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
      }
    };

    const contactSection = document.getElementById('trial-section');
    if (contactSection) {
      contactSection.addEventListener('mousemove', handleMouseMove);
      return () => contactSection.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);


  return (
    <>
      <Navbar hideLoginButton={false} enableScrollEffect={true} />
      
      <Box
        id="trial-section"
        sx={{
          width: '100vw',
          height: '100vh',
          background: '#3044A9',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          cursor: 'none',
          pt: { xs: 10, sm: 12 }, // Account for navbar height
          pb: 7,
        }}
      >
        {/* Enhanced Grid Pattern Background - Full Coverage */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.15) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.15) 1px, transparent 1px)
          `,
            backgroundSize: '80px 80px',
            opacity: 0.25,
          }}
        />

        {/* Cursor Following Grid Highlight */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
            backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px)
          `,
            backgroundSize: '80px 80px',
            maskImage: `radial-gradient(350px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
            WebkitMaskImage: `radial-gradient(350px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
            transition: 'mask-image 0.1s ease-out, -webkit-mask-image 0.1s ease-out',
          }}
        />

        {/* Fine Grid Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px)
          `,
            backgroundSize: '20px 20px',
            opacity: 0.2,
          }}
        />

        {/* Cursor Following Fine Grid */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
            backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.4) 1px, transparent 1px)
          `,
            backgroundSize: '20px 20px',
            maskImage: `radial-gradient(200px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
            WebkitMaskImage: `radial-gradient(200px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
            transition: 'mask-image 0.1s ease-out, -webkit-mask-image 0.1s ease-out',
          }}
        />

        {/* Cursor Glow Effect */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 3,
            background: `radial-gradient(400px circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(255, 255, 255, 0.06) 0%, transparent 70%)`,
            transition: 'background 0.2s ease-out',
            pointerEvents: 'none',
          }}
        />

        {/* Animated Grid Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            backgroundImage: `
            radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.06) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(255, 255, 255, 0.04) 0%, transparent 50%)
          `,
            animation: 'gridPulse 8s ease-in-out infinite',
            '@keyframes gridPulse': {
              '0%, 100%': {
                opacity: 0.3,
              },
              '50%': {
                opacity: 0.6,
              },
            },
          }}
        />

        {/* Depth and Glow Effects */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            background: `
            radial-gradient(ellipse at center, rgba(255, 255, 255, 0.02) 0%, transparent 70%),
            linear-gradient(45deg, rgba(255, 255, 255, 0.03) 0%, transparent 50%),
            linear-gradient(-45deg, rgba(255, 255, 255, 0.02) 0%, transparent 50%)
          `,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '10%',
              left: '10%',
              right: '10%',
              bottom: '10%',
              background: 'radial-gradient(ellipse at center, rgba(255, 255, 255, 0.05) 0%, transparent 60%)',
              borderRadius: '50%',
              filter: 'blur(40px)',
              animation: 'float 6s ease-in-out infinite',
            },
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px) scale(1)',
                opacity: 0.3,
              },
              '50%': {
                transform: 'translateY(-10px) scale(1.05)',
                opacity: 0.5,
              },
            },
          }}
        />

        <Container
  maxWidth="sm"
  disableGutters
  sx={{
    minHeight: { xs: '100dvh', md: '90vh', lg: '90vh' },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#3044A9', 
    position: 'relative',
    zIndex: 10,
  }}
>
  <Paper
    elevation={6}
    sx={{
      width: '100%',
      maxWidth: 700,         
      borderRadius: 1.5,
      p: { xs: 3, sm: 4, md: 5 },
      bgcolor: '#f2f2f2', 
    }}
  >
        <Typography variant="h6" align="center" sx={{ fontWeight: 700, mb: 1 }}>
          Request a Demo and Experience
          <br />
          Seamless Data Integration
        </Typography>

        <Typography
          variant="body2"
          align="center"
          sx={{ color: 'text.secondary', mb: 3 }}
        >
          Help us understand your goals so we can customize your demo
          experience.
        </Typography>

        <Box component="form" noValidate onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Full Name
              </Typography>
              <StyledTextField
                fullWidth
                size="small"
                placeholder="Enter Full Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                inputProps={{ 'aria-label': 'Full Name' }}
              />
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Email
              </Typography>
              <StyledTextField
                fullWidth
                size="small"
                placeholder="Enter Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                inputProps={{ 'aria-label': 'Email' }}
              />
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Tell us your goals for this trial so we can tailor the
                experience to your needs.
              </Typography>
              <StyledTextField
                fullWidth
                size="small"
                placeholder="Type"
                name="goals"
                value={form.goals}
                onChange={handleChange}
                multiline
                minRows={3}
              />
            </Box>

             <Button
              type="submit"
              fullWidth
              disabled={loading}
              sx={{
                mt: 1,
                py: 1,
                fontWeight: 600,
                color: '#fff',
                bgcolor: '#5965A6',
                '&:hover': { bgcolor: '#4C5B9A' },
                opacity: loading ? 0.8 : 1,
              }}
            >
              {loading ? 'Submitting…' : 'Get Started Now'}
            </Button>

            <Typography
              variant="caption"
              align="center"
              sx={{ color: 'text.secondary', fontSize: '0.7rem', mt: 0.5 }}
            >
              By continuing, you agree to our <b>Privacy Policy</b>.
            </Typography>
          </Stack>
        </Box>
      </Paper>
      <Snackbar
        open={notice.open}
        autoHideDuration={2000}
        onClose={() => setNotice((n) => ({ ...n, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotice((n) => ({ ...n, open: false }))}
          severity={notice.type}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notice.msg}
        </Alert>
      </Snackbar>
    {/* </Box> */}
        </Container>
      </Box>
    </>
  );
};


export default FreeTrial;